import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  Upload,
  Download,
  Save,
  X,
  BookOpen,
  RefreshCw,
  Search,
  Tag,
  Clock,
  Filter,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ActivitiesAdmin = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user has @yomev.org email
  const isYomevUser = user?.email?.toLowerCase().endsWith('@yomev.org');
  
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showTagsPanel, setShowTagsPanel] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [allTags, setAllTags] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    ageGroup: '3-6',
    developmentAreas: '',
    competencies: '',
    tags: '',
    materials: '',
    duration: '',
    difficulty: 'orta'
  });

  const developmentAreaOptions = ['Bilişsel', 'Dil', 'Sosyal-Duygusal', 'Motor', 'Özbakım'];
  const difficultyOptions = [
    { value: 'kolay', label: 'Kolay', color: 'bg-green-100 text-green-700' },
    { value: 'orta', label: 'Orta', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'zor', label: 'Zor', color: 'bg-red-100 text-red-700' }
  ];

  useEffect(() => {
    if (!isYomevUser) {
      navigate('/plan-studio');
      return;
    }
  }, [isYomevUser, navigate]);

  useEffect(() => {
    fetchActivities();
  }, [filterArea, filterDifficulty, searchQuery, filterTag]);

  // Extract all unique tags from activities
  useEffect(() => {
    const tagCounts = {};
    activities.forEach(a => {
      (a.tags || []).forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    const sortedTags = Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([tag, count]) => ({ tag, count }));
    setAllTags(sortedTags);
  }, [activities]);

  const fetchActivities = async () => {
    try {
      let url = `${API}/api/plan-studio/activities?limit=100`;
      if (filterArea) url += `&development_area=${filterArea}`;
      if (filterDifficulty) url += `&difficulty=${filterDifficulty}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (filterTag) url += `&tag=${encodeURIComponent(filterTag)}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setActivities(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        ageGroup: formData.ageGroup,
        developmentAreas: formData.developmentAreas.split(',').map(s => s.trim()).filter(Boolean),
        competencies: formData.competencies.split(',').map(s => s.trim()).filter(Boolean),
        tags: formData.tags.split(',').map(s => s.trim()).filter(Boolean),
        materials: formData.materials.split(',').map(s => s.trim()).filter(Boolean),
        duration: formData.duration ? parseInt(formData.duration) : null,
        difficulty: formData.difficulty
      };

      const res = await fetch(`${API}/api/plan-studio/activities`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        await fetchActivities();
        setShowModal(false);
        resetForm();
      } else {
        const err = await res.json();
        alert(err.detail || 'Kaydetme hatası');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/api/plan-studio/activities/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const result = await res.json();
        alert(`${result.importedCount} etkinlik içe aktarıldı`);
        await fetchActivities();
        setShowImportModal(false);
      } else {
        const err = await res.json();
        alert(err.detail || 'İçe aktarma hatası');
      }
    } catch (error) {
      alert('Bağlantı hatası');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      ageGroup: '3-6',
      developmentAreas: '',
      competencies: '',
      tags: '',
      materials: '',
      duration: '',
      difficulty: 'orta'
    });
    setEditingId(null);
  };

  const exportToCSV = () => {
    const headers = ['title', 'description', 'ageGroup', 'developmentAreas', 'competencies', 'tags', 'materials', 'duration', 'difficulty'];
    const rows = activities.map(a => [
      a.title || '',
      a.description || '',
      a.ageGroup || '3-6',
      (a.developmentAreas || []).join('; '),
      (a.competencies || []).join('; '),
      (a.tags || []).join('; '),
      (a.materials || []).join('; '),
      a.duration || '',
      a.difficulty || 'orta'
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'etkinlik_kutuphanesi.csv';
    link.click();
  };

  const openEditModal = (activity) => {
    setFormData({
      title: activity.title,
      description: activity.description || '',
      ageGroup: activity.ageGroup,
      developmentAreas: (activity.developmentAreas || []).join(', '),
      competencies: (activity.competencies || []).join(', '),
      tags: (activity.tags || []).join(', '),
      materials: (activity.materials || []).join(', '),
      duration: activity.duration || '',
      difficulty: activity.difficulty || 'orta'
    });
    setEditingId(activity.id);
    setShowModal(true);
  };

  const getDifficultyBadge = (difficulty) => {
    const opt = difficultyOptions.find(d => d.value === difficulty) || difficultyOptions[1];
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${opt.color}`}>{opt.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activities-admin">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link
            to="/plan-studio"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft size={20} />
            Plan Studio'ya Dön
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-8 h-8 text-purple-600" />
            Etkinlik Kütüphanesi
          </h1>
          <p className="text-gray-600 mt-1">AI plan oluşturmada kullanılacak etkinlikler</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            data-testid="import-csv-btn"
          >
            <Upload size={18} />
            CSV İçe Aktar
          </button>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            data-testid="export-csv-btn"
          >
            <Download size={18} />
            CSV Dışa Aktar
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            data-testid="new-activity-btn"
          >
            <Plus size={18} />
            Yeni Etkinlik
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Etkinlik ara..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <div>
            <select
              value={filterArea}
              onChange={(e) => setFilterArea(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Tüm Gelişim Alanları</option>
              {developmentAreaOptions.map((area) => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Tüm Zorluklar</option>
              {difficultyOptions.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setShowTagsPanel(!showTagsPanel)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              showTagsPanel || filterTag ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid="toggle-tags-panel"
          >
            <Tag size={16} />
            Etiketler
            {filterTag && <span className="px-1.5 py-0.5 bg-purple-200 rounded text-xs">{filterTag}</span>}
          </button>
          {filterTag && (
            <button
              onClick={() => setFilterTag('')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Filtreyi Temizle
            </button>
          )}
          <div className="text-sm text-gray-500">
            {activities.length} etkinlik
          </div>
        </div>
      </div>

      {/* Tags Panel */}
      {showTagsPanel && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4" data-testid="tags-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Tag size={18} className="text-purple-600" />
              Etiket Kütüphanesi
            </h3>
            <span className="text-sm text-gray-500">{allTags.length} farklı etiket</span>
          </div>
          {allTags.length === 0 ? (
            <p className="text-sm text-gray-500">Henüz etiket eklenmemiş</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {allTags.map(({ tag, count }) => (
                <button
                  key={tag}
                  onClick={() => setFilterTag(filterTag === tag ? '' : tag)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    filterTag === tag
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-purple-100 hover:text-purple-700'
                  }`}
                  data-testid={`tag-filter-${tag}`}
                >
                  <span>{tag}</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs ${
                    filterTag === tag ? 'bg-purple-500' : 'bg-gray-200'
                  }`}>
                    {count}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Activities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activities.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white rounded-xl border border-gray-200">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Henüz etkinlik eklenmemiş</p>
          </div>
        ) : (
          activities.map((activity) => (
            <div
              key={activity.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:border-purple-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-medium text-gray-900">{activity.title}</h3>
                {getDifficultyBadge(activity.difficulty)}
              </div>
              
              {activity.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{activity.description}</p>
              )}
              
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Tag size={14} />
                  <span>Yaş: {activity.ageGroup}</span>
                  {activity.duration && (
                    <>
                      <Clock size={14} className="ml-2" />
                      <span>{activity.duration} dk</span>
                    </>
                  )}
                </div>
                
                {activity.developmentAreas?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {activity.developmentAreas.map((area, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                        {area}
                      </span>
                    ))}
                  </div>
                )}
                
                {activity.materials?.length > 0 && (
                  <p className="text-xs text-gray-500">
                    <strong>Materyaller:</strong> {activity.materials.slice(0, 3).join(', ')}
                    {activity.materials.length > 3 && '...'}
                  </p>
                )}

                {/* Clickable Tags */}
                {activity.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
                    {activity.tags.map((tag, idx) => (
                      <button
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFilterTag(tag);
                          setShowTagsPanel(true);
                        }}
                        className={`px-2 py-0.5 rounded text-xs transition-colors ${
                          filterTag === tag
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:text-purple-700'
                        }`}
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => openEditModal(activity)}
                  className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  <Edit2 size={14} />
                  Düzenle
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Etkinliği Düzenle' : 'Yeni Etkinlik Ekle'}
              </h3>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Etkinlik Adı *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Örn: Tanışma Çemberi, Parmak Boyası..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                  placeholder="Etkinliğin kısa açıklaması..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yaş Grubu</label>
                  <select
                    value={formData.ageGroup}
                    onChange={(e) => setFormData({ ...formData, ageGroup: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="3-4">3-4 yaş</option>
                    <option value="4-5">4-5 yaş</option>
                    <option value="5-6">5-6 yaş</option>
                    <option value="3-6">3-6 yaş (Tümü)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zorluk</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {difficultyOptions.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Süre (dk)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="15"
                    min="1"
                    max="120"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gelişim Alanları (virgülle ayırın)</label>
                <input
                  type="text"
                  value={formData.developmentAreas}
                  onChange={(e) => setFormData({ ...formData, developmentAreas: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Bilişsel, Dil, Sosyal-Duygusal, Motor, Özbakım"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kazanımlar (virgülle ayırın)</label>
                <textarea
                  value={formData.competencies}
                  onChange={(e) => setFormData({ ...formData, competencies: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                  placeholder="İlgili kazanımlar..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Materyaller (virgülle ayırın)</label>
                <input
                  type="text"
                  value={formData.materials}
                  onChange={(e) => setFormData({ ...formData, materials: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Renkli kağıt, makas, yapıştırıcı..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Etiketler (virgülle ayırın)</label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="sanat, müzik, hareket, doğa..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                İptal
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.title}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <RefreshCw size={18} className="animate-spin" /> : <Save size={18} />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">CSV Dosyası İçe Aktar</h3>
              <button onClick={() => setShowImportModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                CSV dosyası şu sütunları içermelidir:<br/>
                <code className="text-xs bg-gray-100 px-1 rounded">title, description, ageGroup, developmentAreas, competencies, tags, materials, duration, difficulty</code>
              </p>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImport}
                  className="hidden"
                  id="activity-csv-upload"
                />
                <label
                  htmlFor="activity-csv-upload"
                  className="cursor-pointer text-purple-600 hover:text-purple-700 font-medium"
                >
                  CSV dosyası seçin
                </label>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivitiesAdmin;
